import React, { useState } from 'react';
import { X } from 'lucide-react';
import { TableConfig, TableSelection, BorderStyle } from '@/app/types/table';

interface BorderStylePanelProps {
  selection: TableSelection | null;
  tableConfig: TableConfig;
  onUpdateConfig: (config: TableConfig) => void;
  onClose: () => void;
}

export function BorderStylePanel({
  selection,
  tableConfig,
  onUpdateConfig,
  onClose,
}: BorderStylePanelProps) {
  const [borderSide, setBorderSide] = useState<'all' | 'top' | 'right' | 'bottom' | 'left'>('all');
  const [borderWidth, setBorderWidth] = useState(1);
  const [borderColor, setBorderColor] = useState('#000000');
  const [borderStyle, setBorderStyle] = useState<BorderStyle>('solid');
  const [borderVisible, setBorderVisible] = useState(true);

  const handleApply = () => {
    if (!selection) return;

    const newConfig = { ...tableConfig };
    const { startRow, startCol, endRow, endCol } = selection;

    // Apply border style to selected cells
    for (let r = startRow; r <= endRow; r++) {
      for (let c = startCol; c <= endCol; c++) {
        const row = newConfig.rows[r];
        const colId = newConfig.columns[c]?.id;
        
        if (!row || !colId || !row.data[colId]) continue;

        const cell = row.data[colId];
        const currentStyle = cell.style || {};
        const currentBorders = currentStyle.borders || {};

        const borderConfig = {
          visible: borderVisible,
          width: borderWidth,
          color: borderColor,
          style: borderStyle,
        };

        // Update borders based on selected side
        const newBorders = { ...currentBorders };
        
        if (borderSide === 'all') {
          newBorders.top = borderConfig;
          newBorders.right = borderConfig;
          newBorders.bottom = borderConfig;
          newBorders.left = borderConfig;
        } else {
          newBorders[borderSide] = borderConfig;
        }

        // Update cell style
        newConfig.rows[r].data[colId] = {
          ...cell,
          style: {
            ...currentStyle,
            borders: newBorders,
          },
        };
      }
    }

    onUpdateConfig(newConfig);
    onClose();
  };

  return (
    <div
      className="absolute top-12 right-4 rounded-lg shadow-xl p-4 w-80 z-50"
      style={{
        backgroundColor: 'var(--md-surface-container-high)',
        border: '1px solid var(--md-outline-variant)',
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold">Rahmen bearbeiten</h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-opacity-10"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {!selection && (
        <div className="text-sm mb-4" style={{ color: 'var(--md-error)' }}>
          Bitte wählen Sie Zellen aus
        </div>
      )}

      <div className="space-y-4">
        {/* Border Side */}
        <div>
          <label className="block text-sm font-medium mb-2">Seite</label>
          <div className="grid grid-cols-5 gap-1">
            {(['all', 'top', 'right', 'bottom', 'left'] as const).map((side) => (
              <button
                key={side}
                onClick={() => setBorderSide(side)}
                className="p-2 rounded text-xs font-medium"
                style={{
                  backgroundColor: borderSide === side 
                    ? 'var(--md-primary)' 
                    : 'var(--md-surface-variant)',
                  color: borderSide === side 
                    ? 'var(--md-on-primary)' 
                    : 'var(--md-on-surface-variant)',
                }}
              >
                {side === 'all' ? 'Alle' : 
                 side === 'top' ? 'Oben' : 
                 side === 'right' ? 'Rechts' : 
                 side === 'bottom' ? 'Unten' : 'Links'}
              </button>
            ))}
          </div>
        </div>

        {/* Visibility */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="border-visible"
            checked={borderVisible}
            onChange={(e) => setBorderVisible(e.target.checked)}
            className="w-4 h-4"
          />
          <label htmlFor="border-visible" className="text-sm">
            Rahmen anzeigen
          </label>
        </div>

        {/* Border Width */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Stärke: {borderWidth}px
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={borderWidth}
            onChange={(e) => setBorderWidth(Number(e.target.value))}
            className="w-full"
            disabled={!borderVisible}
          />
        </div>

        {/* Border Color */}
        <div>
          <label className="block text-sm font-medium mb-2">Farbe</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={borderColor}
              onChange={(e) => setBorderColor(e.target.value)}
              className="w-12 h-8 rounded cursor-pointer"
              disabled={!borderVisible}
            />
            <input
              type="text"
              value={borderColor}
              onChange={(e) => setBorderColor(e.target.value)}
              className="flex-1 px-2 py-1 rounded border text-sm"
              style={{
                backgroundColor: 'var(--md-surface)',
                borderColor: 'var(--md-outline)',
              }}
              disabled={!borderVisible}
            />
          </div>
        </div>

        {/* Border Style */}
        <div>
          <label className="block text-sm font-medium mb-2">Stil</label>
          <select
            value={borderStyle}
            onChange={(e) => setBorderStyle(e.target.value as BorderStyle)}
            className="w-full px-3 py-2 rounded border"
            style={{
              backgroundColor: 'var(--md-surface)',
              borderColor: 'var(--md-outline)',
            }}
            disabled={!borderVisible}
          >
            <option value="solid">Durchgezogen</option>
            <option value="dashed">Gestrichelt</option>
            <option value="dotted">Gepunktet</option>
            <option value="double">Doppelt</option>
            <option value="none">Keine</option>
          </select>
        </div>

        {/* Preview */}
        <div>
          <label className="block text-sm font-medium mb-2">Vorschau</label>
          <div
            className="w-full h-16 rounded"
            style={{
              backgroundColor: '#ffffff',
              borderTop: borderVisible && (borderSide === 'all' || borderSide === 'top')
                ? `${borderWidth}px ${borderStyle} ${borderColor}`
                : '1px solid #e0e0e0',
              borderRight: borderVisible && (borderSide === 'all' || borderSide === 'right')
                ? `${borderWidth}px ${borderStyle} ${borderColor}`
                : '1px solid #e0e0e0',
              borderBottom: borderVisible && (borderSide === 'all' || borderSide === 'bottom')
                ? `${borderWidth}px ${borderStyle} ${borderColor}`
                : '1px solid #e0e0e0',
              borderLeft: borderVisible && (borderSide === 'all' || borderSide === 'left')
                ? `${borderWidth}px ${borderStyle} ${borderColor}`
                : '1px solid #e0e0e0',
            }}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded"
            style={{
              backgroundColor: 'var(--md-surface-variant)',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleApply}
            disabled={!selection}
            className="flex-1 px-4 py-2 rounded"
            style={{
              backgroundColor: selection ? 'var(--md-primary)' : 'var(--md-surface-variant)',
              color: selection ? 'var(--md-on-primary)' : 'var(--md-on-surface-variant)',
              opacity: selection ? 1 : 0.5,
            }}
          >
            Anwenden
          </button>
        </div>
      </div>
    </div>
  );
}
